/**
 * Browser & Context Data Collection for Scoring
 * Collects device, location, traffic data for ML scoring
 */

/**
 * Collect comprehensive browser and context data for scoring
 * @param {object} wpContext - WordPress context from wp_localize_script (optional)
 * @returns {object} Complete scoring data payload
 */
export function collectScoringData(wpContext = null) {
    const data = {
        // Session & Tenant
        session_id: getOrCreateSessionId(),
        tenant_id: window.configData?.TENANTID || null,

        // Device Context
        device: {
            type: detectDeviceType(),
            screen: `${window.screen.width}x${window.screen.height}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            retina: window.devicePixelRatio > 1,
            pixelRatio: window.devicePixelRatio,
            touch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
            maxTouchPoints: navigator.maxTouchPoints || 0,
            platform: navigator.platform || 'unknown',
        },

        // Location Context
        location: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timezoneOffset: new Date().getTimezoneOffset(),
            language: navigator.language || 'unknown',
            languages: navigator.languages || [navigator.language],
            // Country/City will be determined server-side from IP
        },

        // Traffic Context
        traffic: {
            referrer: document.referrer || null,
            currentUrl: window.location.href,
            currentPage: window.location.pathname,
            utm: extractUTMParams(),
            isReturning: checkIfReturningUser(),
        },

        // Connection Quality (if available - Chrome/Edge)
        connection: navigator.connection ? {
            effectiveType: navigator.connection.effectiveType || 'unknown', // "4g", "3g", etc.
            downlink: navigator.connection.downlink || null, // Mbps
            rtt: navigator.connection.rtt || null, // Round-trip time (ms)
            saveData: navigator.connection.saveData || false, // Data saver active?
        } : null,

        // WordPress Context (if available)
        wp_context: wpContext,

        // Raw Browser Data
        browser_data: {
            userAgent: navigator.userAgent,
            vendor: navigator.vendor || 'unknown',
            cookieEnabled: navigator.cookieEnabled,
            doNotTrack: navigator.doNotTrack || null,
            onLine: navigator.onLine,
            hardwareConcurrency: navigator.hardwareConcurrency || null, // CPU cores
            maxTouchPoints: navigator.maxTouchPoints || 0,
        },

        // Timestamp
        timestamp: new Date().toISOString(),
    };

    return data;
}

/**
 * Detect device type from user agent and screen size
 * @returns {string} "mobile", "tablet", or "desktop"
 */
function detectDeviceType() {
    const ua = navigator.userAgent.toLowerCase();
    const isMobile = /mobile|android|iphone/i.test(ua);
    const isTablet = /ipad|tablet/i.test(ua);

    // Also check screen size
    const isSmallScreen = window.innerWidth < 768;
    const isMediumScreen = window.innerWidth >= 768 && window.innerWidth < 1024;

    if (isTablet || isMediumScreen) {
        return 'tablet';
    } else if (isMobile || isSmallScreen) {
        return 'mobile';
    }

    return 'desktop';
}

/**
 * Extract UTM parameters from URL
 * @returns {object} UTM parameters
 */
function extractUTMParams() {
    const params = new URLSearchParams(window.location.search);

    return {
        source: params.get('utm_source') || null,
        medium: params.get('utm_medium') || null,
        campaign: params.get('utm_campaign') || null,
        content: params.get('utm_content') || null,
        term: params.get('utm_term') || null,
    };
}

/**
 * Check if user is returning (via localStorage)
 * @returns {boolean} True if returning user
 */
function checkIfReturningUser() {
    const storageKey = 'ibe_returning_user';
    const isReturning = localStorage.getItem(storageKey) === 'true';

    // Mark as returning for next visit
    localStorage.setItem(storageKey, 'true');

    return isReturning;
}

/**
 * Get or create session ID (from localStorage)
 * @returns {string} Session ID
 */
function getOrCreateSessionId() {
    const storageKey = 'ibe_session_id';
    let sessionId = localStorage.getItem(storageKey);

    if (!sessionId) {
        // Generate UUID v4
        sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });

        localStorage.setItem(storageKey, sessionId);
    }

    return sessionId;
}

/**
 * Send scoring data to backend
 * @param {object} scoringData - Scoring data payload
 * @param {string} apiUrl - API base URL (ignored - scoring goes direct to pyserver)
 * @returns {Promise<object>} Response from server
 */
export async function sendScoringData(scoringData, apiUrl) {
    // Scoring goes DIRECT to pyserver (like banners, teaser plugin)
    // Not through amadeus proxy - pyserver handles session/scoring
    const IBE_PYSERVER_URL = 'https://www2.tstsrv.de/ibeapi';
    const endpoint = `${IBE_PYSERVER_URL}/api/v1/scoring/init`;

    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'TenantID': window.configData?.TENANTID || '',
                'App': window.configData?.APP || 'wordpress-widget',
            },
            body: JSON.stringify(scoringData),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log('[IBE Scoring] Initialized:', result);

        return result;
    } catch (error) {
        console.error('[IBE Scoring] Failed to initialize:', error);
        // Don't throw - scoring is optional, widget should still work
        return null;
    }
}
